import { useCallback, useMemo, useState } from "react";
import { QuestionResponse, Responses } from "@/pages/deals/types";

function valuesMatch(
  serverResponse: QuestionResponse | null | undefined,
  overrideResponse: QuestionResponse | null | undefined
): boolean {
  const serverValue = serverResponse?.response?.value;
  const overrideValue = overrideResponse?.response?.value;
  if (serverValue === overrideValue) return true;
  if (Array.isArray(serverValue) && Array.isArray(overrideValue)) {
    if (serverValue.length !== overrideValue.length) return false;
    const sortedServer = [...serverValue].sort();
    const sortedOverride = [...overrideValue].sort();
    return sortedServer.every(
      (value, index) => value === sortedOverride[index]
    );
  }
  return false;
}

export function useQuestionnaireResponses(serverResponses?: Responses) {
  const [localOverrides, setLocalOverrides] = useState<Responses>({});

  const localResponses = useMemo(() => {
    const server = serverResponses || {};
    const merged: Responses = { ...server };
    for (const questionId of Object.keys(localOverrides)) {
      const override = localOverrides[questionId];
      const serverQuestion = server[questionId];

      // Server caught up to the optimistic value — use full server data
      // which includes enriched fields (person, candidateId, lastEditedBy, etc.)
      if (override && serverQuestion && valuesMatch(serverQuestion, override)) {
        continue;
      }

      if (override === null) {
        merged[questionId] = null;
      } else {
        // Deep merge: preserve server enriched fields at both levels
        merged[questionId] = {
          ...(serverQuestion || {}),
          ...override,
          response: {
            ...(serverQuestion?.response || {}),
            ...(override?.response || {})
          }
        } as QuestionResponse;
      }
    }
    return merged;
  }, [serverResponses, localOverrides]);

  const updateResponse = useCallback(
    (questionId: string, response: QuestionResponse | null) => {
      setLocalOverrides((prev) => ({ ...prev, [questionId]: response }));
    },
    []
  );

  const resetResponses = useCallback(() => {
    setLocalOverrides({});
  }, []);

  return { localResponses, updateResponse, resetResponses };
}
